import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/** CSS px per mm (96 dpi). */
const PX_PER_MM = 96 / 25.4

/**
 * Shows a paper-sized document scaled down to the width of its pane, so the
 * whole page is visible at once and looks exactly as it prints — the
 * document itself is laid out at real size and only transformed.
 *
 * `paperWidthMm` is the sheet's width; the wrapper reserves the scaled height
 * so the page does not overlap what follows it. Never scales up.
 */
export function PoScaledPreview({ paperWidthMm, children }: { paperWidthMm: number; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [height, setHeight] = useState<number | null>(null)
  const pageWidthPx = paperWidthMm * PX_PER_MM

  useLayoutEffect(() => {
    const o = outer.current
    const i = inner.current
    if (!o || !i) return
    const measure = () => {
      const s = Math.min(1, o.clientWidth / pageWidthPx)
      setScale(s)
      setHeight(i.offsetHeight * s)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(o)
    ro.observe(i)
    return () => ro.disconnect()
  }, [pageWidthPx])

  return (
    <div ref={outer} className='w-full overflow-hidden' style={{ height: height ?? undefined }}>
      <div ref={inner} style={{ width: pageWidthPx, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  )
}
